/**
 * 楼层呼叫与候梯队列渲染器（设计文档 F-016 / floor-panel-renderer.ts）。
 *
 * 绘制各楼层：楼层号、上行/下行呼叫灯、候梯人数、最长等待超时高亮。
 */
import { THEME } from './theme';
import type { FloorSnapshot } from '../core/snapshot';

export interface FloorPanelLayout {
  x: number;
  y: number;
  width: number;
  height: number;
  /** 等待超过该阈值（ms）的楼层高亮告警（来自策略参数/默认）。 */
  waitWarnMs?: number;
}

export function renderFloorPanel(
  ctx: CanvasRenderingContext2D,
  floors: readonly FloorSnapshot[],
  layout: FloorPanelLayout,
): void {
  const { x, y, width: w, height: h, waitWarnMs } = layout;
  const n = Math.max(1, floors.length);
  const rowH = h / n;
  const r = Math.min(5, rowH * 0.32); // 呼叫灯半径

  ctx.font = `${Math.max(9, Math.floor(rowH * 0.42))}px ui-monospace, monospace`;
  ctx.textBaseline = 'middle';

  // 顶楼在上方：floors 数组下标 0=底层，倒序绘制
  for (let i = 0; i < n; i++) {
    const f = floors[floors.length - 1 - i]!;
    const ry = y + i * rowH + rowH / 2;
    const longWait = waitWarnMs !== undefined && f.longestWaitMs >= waitWarnMs && f.waitingCount > 0;

    // 行底分隔
    ctx.strokeStyle = THEME.grid;
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(x, y + (i + 1) * rowH);
    ctx.lineTo(x + w, y + (i + 1) * rowH);
    ctx.stroke();

    // 楼层号
    ctx.fillStyle = longWait ? THEME.waitWarn : THEME.textDim;
    ctx.textAlign = 'left';
    ctx.fillText(`F${f.floor}`, x + 4, ry);

    // 呼叫灯：上/下
    const cx = x + w - 14;
    drawLight(ctx, cx, ry - rowH * 0.18, r, f.upCall, THEME.up);
    drawLight(ctx, cx, ry + rowH * 0.18, r, f.downCall, THEME.down);

    // 候梯人数
    ctx.fillStyle = longWait ? THEME.waitWarn : THEME.text;
    ctx.textAlign = 'right';
    if (f.waitingCount > 0) ctx.fillText(`${f.waitingCount}`, x + w - 30, ry);
  }

  // 表头：上/下标记
  ctx.fillStyle = THEME.textDim;
  ctx.textAlign = 'center';
  ctx.fillText('▲▼', x + w - 14, y - rowH * 0.3);
}

function drawLight(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
  on: boolean,
  color: string,
): void {
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fillStyle = on ? color : '#2a313c';
  ctx.fill();
  if (on) {
    ctx.strokeStyle = color;
    ctx.lineWidth = 1;
    ctx.stroke();
  }
}
